// Plan géographique d'un marché à ciel ouvert (D-201 à D-217, plan précis D-300 à D-308) : points
// regroupés servis par emprise, allées, contours des îlots et rectangles des places au zoom fin ;
// en édition, tracé d'une allée ou d'un îlot (quatre coins), déplacement et ajustements de la place
// sélectionnée, rangées le long d'une allée, imports. Fond OpenStreetMap seulement.
import { Bandeau, Bouton, CarteTerritoire, Champ, Selection, type Territoire } from '@cc/ui';
import type { FeatureCollection, LineString, Point } from 'geojson';
import { useMemo, useState } from 'react';
import { messageErreur } from '../../api/erreurs';
import { useAction } from '../../commun/useAction';
import { INVALIDER_MARCHES } from './api';
import { AjustementsPlace } from './AjustementsPlace';
import { EditeurIlot } from './EditeurIlot';
import { EditeurRangee } from './EditeurRangee';
import { ImportAllees } from './ImportAllees';
import { ImportCommercants, ImportPlan } from './ImportsPlan';
import { ListeIlots } from './ListeIlots';
import { creerAllee, deplacerPlace, useAllees, usePlaces } from './geographie-api';
import { usePlanPrecis } from './plan-api';
import type { Emplacement, MarcheDetail } from './types';
import styles from './marches.module.css';

type Bbox = [number, number, number, number];
type Position = [number, number];
type Mode = 'aucun' | 'allee' | 'ilot';

function empriseInitiale(m: MarcheDetail): Bbox | null {
  const points = m.emplacements.flatMap((e) =>
    e.position ? [[e.position.longitude, e.position.latitude] as Position] : [],
  );
  if (m.position) points.push([m.position.longitude, m.position.latitude]);
  if (!points.length) return null;
  let o = 180;
  let s = 90;
  let e = -180;
  let n = -90;
  // Boucle plutôt que Math.min(...) : 10 000 places dépasseraient la pile d'arguments.
  for (const [x, y] of points) {
    o = Math.min(o, x);
    s = Math.min(s, y);
    e = Math.max(e, x);
    n = Math.max(n, y);
  }
  return [o - 0.001, s - 0.001, e + 0.001, n + 0.001];
}

export function PlanGeographique({
  marche,
  edition,
  selection,
  emplacements,
  onSelection,
}: {
  marche: MarcheDetail;
  edition: boolean;
  selection: Emplacement | null;
  emplacements: Emplacement[];
  onSelection: (id: string) => void;
}) {
  const initiale = useMemo(() => empriseInitiale(marche), [marche]);
  const [bbox, setBbox] = useState<Bbox | null>(initiale);
  const [precision, setPrecision] = useState('toutes');
  const [mode, setMode] = useState<Mode>('aucun');
  const [trace, setTrace] = useState<Position[]>([]);
  const [nom, setNom] = useState('');
  const [position, setPosition] = useState<Position | null>(null);
  const allees = useAllees(marche.id);
  const places = usePlaces(marche.id, bbox);
  const plan = usePlanPrecis(marche.id, bbox);
  const changerMode = (m: Mode) => {
    setMode(mode === m ? 'aucun' : m);
    setTrace([]);
    setPosition(null);
  };
  const creer = useAction({
    executer: () =>
      creerAllee(marche.id, { nom, ligne: { type: 'LineString', coordinates: trace } }),
    succes: 'Allée enregistrée.',
    invalider: INVALIDER_MARCHES,
    apres: () => {
      setTrace([]);
      setMode('aucun');
      setNom('');
    },
  });
  const deplacer = useAction({
    executer: () =>
      deplacerPlace(selection!.id, {
        position: { longitude: position![0], latitude: position![1] },
        version_attendue: selection!.position_version ?? 0,
      }),
    succes: 'Position schématique enregistrée.',
    invalider: INVALIDER_MARCHES,
    apres: () => setPosition(null),
  });
  const territoire = useMemo<Territoire | null>(
    () =>
      initiale
        ? {
            id: marche.id,
            niveau: 'commune',
            libelle: marche.nom,
            geometrie: {
              type: 'Polygon',
              coordinates: [
                [
                  [initiale[0], initiale[1]],
                  [initiale[2], initiale[1]],
                  [initiale[2], initiale[3]],
                  [initiale[0], initiale[3]],
                  [initiale[0], initiale[1]],
                ],
              ],
            },
            indicatif: true,
            source: 'Emprise indicative des positions enregistrées',
            voisins: [],
          }
        : null,
    [initiale, marche.id, marche.nom],
  );
  const visibles = useMemo(() => new Set(emplacements.map((e) => e.id)), [emplacements]);
  const points = useMemo<FeatureCollection<Point>>(
    () => ({
      type: 'FeatureCollection',
      features: (places.data?.features ?? []).filter(
        (f) =>
          visibles.has(String(f.properties?.id)) &&
          (precision === 'toutes' || f.properties?.precision === precision),
      ),
    }),
    [places.data, visibles, precision],
  );
  const formes = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: (plan.data?.formes.features ?? []).filter((f) =>
        visibles.has(String(f.properties?.id)),
      ),
    }),
    [plan.data, visibles],
  );
  const lignes = useMemo<FeatureCollection<LineString>>(() => {
    const enCours = mode === 'ilot' && trace.length === 4 ? [...trace, trace[0]!] : trace;
    return {
      type: 'FeatureCollection',
      features: [
        ...(allees.data ?? []).map((a) => ({
          type: 'Feature' as const,
          id: a.id,
          geometry: a.ligne,
          properties: { nom: a.nom },
        })),
        ...(enCours.length > 1
          ? [
              {
                type: 'Feature' as const,
                id: 'trace-en-cours',
                geometry: { type: 'LineString' as const, coordinates: enCours },
                properties: { nom: 'Tracé en cours' },
              },
            ]
          : []),
      ],
    };
  }, [allees.data, trace, mode]);
  const erreur = places.error ?? allees.error ?? plan.error;

  return (
    <div className={styles.pile}>
      <Selection
        libelle="Précision des positions"
        value={precision}
        onChange={(e) => setPrecision(e.target.value)}
        options={[
          { valeur: 'toutes', libelle: 'Toutes les positions' },
          { valeur: 'schematique', libelle: 'Schématiques' },
          { valeur: 'terrain', libelle: 'Relevées sur le terrain' },
        ]}
      />
      {erreur ? <Bandeau type="erreur">{messageErreur(erreur)}</Bandeau> : null}
      {territoire ? (
        <CarteTerritoire
          titre="Places du marché"
          echelles={[territoire]}
          echelle={territoire.id}
          onEchelle={() => undefined}
          compact
          couchePoints={{
            points,
            allees: lignes,
            ilots: plan.data?.ilots,
            formes,
            onPoint: (id) => {
              setPosition(null);
              onSelection(id);
            },
            onEmprise: setBbox,
          }}
          onSaisie={
            edition
              ? (p) => {
                  if (mode === 'allee') setTrace((l) => [...l, p]);
                  else if (mode === 'ilot') setTrace((l) => (l.length < 4 ? [...l, p] : l));
                  else if (selection) setPosition(p);
                }
              : undefined
          }
        />
      ) : (
        <Bandeau type="info">
          Renseignez la position du marché pour ouvrir son plan géographique.
        </Bandeau>
      )}
      <p className={styles.aide}>
        Les places schématiques sont à confirmer sur le terrain. Les rectangles des places
        apparaissent en rapprochant la carte. La liste des emplacements reste disponible sans carte.
      </p>
      <ListeIlots marcheId={marche.id} edition={edition} />
      {edition ? (
        <>
          <div className={styles.barreFiltres}>
            <Bouton
              variante="secondaire"
              aria-pressed={mode === 'allee'}
              onClick={() => changerMode('allee')}
            >
              Tracer une allée
            </Bouton>
            <Bouton
              variante="secondaire"
              aria-pressed={mode === 'ilot'}
              onClick={() => changerMode('ilot')}
            >
              Dessiner un îlot
            </Bouton>
            {mode !== 'aucun' ? (
              <Bouton
                variante="discret"
                disabled={!trace.length}
                onClick={() => setTrace((l) => l.slice(0, -1))}
              >
                Retirer le dernier point
              </Bouton>
            ) : null}
          </div>
          {mode === 'ilot' ? (
            <EditeurIlot marcheId={marche.id} coins={trace} onTermine={() => changerMode('ilot')} />
          ) : null}
          {mode === 'allee' ? (
            <form
              className={styles.pile}
              onSubmit={(e) => {
                e.preventDefault();
                creer.lancer(undefined);
              }}
            >
              <p>Cliquez sur la carte pour dessiner l’allée ({trace.length} points).</p>
              <Champ
                libelle="Nom de l’allée"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                required
                maxLength={120}
              />
              {creer.messageErreur ? <Bandeau type="erreur">{creer.messageErreur}</Bandeau> : null}
              <Bouton type="submit" disabled={trace.length < 2 || creer.enCours}>
                Enregistrer l’allée
              </Bouton>
            </form>
          ) : null}
          {selection && mode === 'aucun' ? (
            <div className={styles.pile}>
              <p>
                Place {selection.reference} : cliquez sur sa nouvelle position dans la carte, puis
                confirmez.
              </p>
              {position ? (
                <p>
                  Longitude {position[0].toFixed(6)}, latitude {position[1].toFixed(6)}.
                </p>
              ) : null}
              {deplacer.messageErreur ? (
                <Bandeau type="erreur">{deplacer.messageErreur}</Bandeau>
              ) : null}
              <Bouton
                disabled={!position || deplacer.enCours}
                onClick={() => deplacer.lancer(undefined)}
              >
                Confirmer le déplacement
              </Bouton>
              <AjustementsPlace
                key={`${selection.id}-${selection.position_version ?? 0}`}
                place={selection}
                emplacements={emplacements}
              />
            </div>
          ) : null}
          <EditeurRangee marcheId={marche.id} allees={allees.data ?? []} />
          <ImportAllees marcheId={marche.id} />
          <ImportPlan marcheId={marche.id} />
          <ImportCommercants marcheId={marche.id} />
        </>
      ) : null}
    </div>
  );
}
